const STORAGE_KEY = 'scc-saved-products-v1';
const MAX_SAVED_PRODUCTS = 50;
const MAX_TEXT_LENGTH = 180;

function updateSaveButtonLabel(button, saved) {
  const span = button.querySelector('[data-scc-save-label]') || button.querySelector('span');
  if (!span) return;
  const galleryLabel = span.dataset.sccSaveLabel === 'Save';
  span.textContent = galleryLabel ? (saved ? 'Saved' : 'Save') : saved ? 'SAVED' : 'WISHLIST';
}

function normalizeText(value, maximumLength = MAX_TEXT_LENGTH) {
  return String(value || '').trim().slice(0, maximumLength);
}

export function normalizeSavedProducts(value) {
  if (!Array.isArray(value)) return [];

  const seen = new Set();
  const products = [];
  for (const item of value) {
    if (!item || typeof item !== 'object') continue;
    const id = normalizeText(item.id, 80);
    const title = normalizeText(item.title);
    const url = normalizeText(item.url, 500);
    if (!id || !title || !url || seen.has(id)) continue;

    seen.add(id);
    products.push({
      id,
      title,
      url,
      image: normalizeText(item.image, 1000),
      imageAlt: normalizeText(item.imageAlt || title),
    });
    if (products.length >= MAX_SAVED_PRODUCTS) break;
  }

  return products;
}

export function safeProductUrl(value, origin) {
  try {
    const url = new URL(value, origin);
    if (url.origin !== origin || !url.pathname.startsWith('/products/')) return null;
    return `${url.pathname}${url.search}`;
  } catch {
    return null;
  }
}

export function safeImageUrl(value, origin) {
  if (!value) return null;
  try {
    const url = new URL(value, origin);
    const trustedHost =
      url.origin === origin ||
      url.hostname === 'cdn.shopify.com' ||
      url.hostname.endsWith('.shopifycdn.com') ||
      url.hostname.endsWith('.shopifycdn.net');
    if (url.protocol !== 'https:' || !trustedHost) return null;
    return url.href;
  } catch {
    return null;
  }
}

const BaseElement = globalThis.HTMLElement ?? class {};

class SccSavedProducts extends BaseElement {
  connectedCallback() {
    this.dialog = this.querySelector('[data-scc-saved-dialog]');
    this.list = this.querySelector('[data-scc-saved-list]');
    this.empty = this.querySelector('[data-scc-saved-empty]');
    this.count = this.querySelector('[data-scc-saved-count]');
    this.status = this.querySelector('[data-scc-saved-status]');
    this.items = this.#read();

    document.addEventListener('click', this.#onDocumentClick);
    window.addEventListener('storage', this.#onStorage);
    document.addEventListener('shopify:section:load', this.#onSectionLoad);
    this.dialog?.addEventListener('click', this.#onDialogClick);
    this.observer = new MutationObserver(this.#onMutations);
    this.observer.observe(document.body, { childList: true, subtree: true });
    this.#render();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.#onDocumentClick);
    window.removeEventListener('storage', this.#onStorage);
    document.removeEventListener('shopify:section:load', this.#onSectionLoad);
    this.dialog?.removeEventListener('click', this.#onDialogClick);
    this.observer?.disconnect();
  }

  #read() {
    try {
      return normalizeSavedProducts(JSON.parse(localStorage.getItem(STORAGE_KEY) || '[]'));
    } catch {
      return [];
    }
  }

  #write(items) {
    this.items = normalizeSavedProducts(items);
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(this.items));
      this.#announce(this.dataset.savedMessage || 'Saved products updated.');
    } catch {
      this.#announce(this.dataset.sessionMessage || 'Saved on this page only.');
    }
    this.#render();
  }

  #announce(message) {
    if (!this.status) return;
    this.status.textContent = '';
    requestAnimationFrame(() => {
      if (this.status) this.status.textContent = message;
    });
  }

  #onStorage = (event) => {
    if (event.key !== STORAGE_KEY) return;
    this.items = this.#read();
    this.#render();
  };

  #onSectionLoad = () => this.#syncButtons();

  #onMutations = (mutations) => {
    const hasSaveButtons = mutations.some((mutation) =>
      [...mutation.addedNodes].some(
        (node) =>
          node instanceof Element &&
          (node.matches('[data-scc-save-product]') || node.querySelector('[data-scc-save-product]'))
      )
    );
    if (hasSaveButtons) this.#syncButtons();
  };

  #onDialogClick = (event) => {
    if (event.target === this.dialog) this.dialog.close();
  };

  #onDocumentClick = (event) => {
    const target = event.target instanceof Element ? event.target : null;
    if (!target) return;

    const openButton = target.closest('[data-scc-saved-open]');
    if (openButton && this.contains(openButton)) {
      event.preventDefault();
      this.#open();
      return;
    }

    const closeButton = target.closest('[data-scc-saved-close]');
    if (closeButton && this.contains(closeButton)) {
      this.dialog?.close();
      return;
    }

    const removeButton = target.closest('[data-scc-saved-remove]');
    if (removeButton && this.contains(removeButton)) {
      const id = removeButton.getAttribute('data-scc-saved-remove');
      this.#write(this.items.filter((item) => item.id !== id));
      return;
    }

    const saveButton = target.closest('[data-scc-save-product]');
    if (!saveButton) return;
    event.preventDefault();
    event.stopPropagation();
    this.#toggle(saveButton);
  };

  #toggle(button) {
    const item = normalizeSavedProducts([
      {
        id: button.dataset.productId,
        title: button.dataset.productTitle,
        url: button.dataset.productUrl,
        image: button.dataset.productImage,
        imageAlt: button.dataset.productImageAlt,
      },
    ])[0];
    if (!item) return;

    const saved = this.items.some((product) => product.id === item.id);
    this.#write(saved ? this.items.filter((product) => product.id !== item.id) : [item, ...this.items]);
  }

  #open() {
    if (!this.dialog) return;
    if (typeof this.dialog.showModal === 'function') this.dialog.showModal();
    else this.dialog.setAttribute('open', '');
  }

  #render() {
    this.#syncButtons();

    if (this.count) {
      this.count.textContent = this.items.length ? String(this.items.length) : '';
      this.count.hidden = this.items.length === 0;
    }

    const trigger = this.querySelector('[data-scc-saved-open]');
    if (trigger) {
      trigger.setAttribute(
        'aria-label',
        this.items.length ? `Saved products, ${this.items.length} items` : 'Saved products, empty'
      );
    }

    if (!this.list || !this.empty) return;
    this.list.replaceChildren(...this.items.map((item) => this.#createItem(item)));
    this.empty.hidden = this.items.length > 0;
    this.list.hidden = this.items.length === 0;
  }

  #syncButtons() {
    const ids = new Set(this.items.map((item) => item.id));
    document.querySelectorAll('[data-scc-save-product]').forEach((button) => {
      const saved = ids.has(button.dataset.productId);
      const title = button.dataset.productTitle || 'product';
      button.setAttribute('aria-pressed', String(saved));
      updateSaveButtonLabel(button, saved);
      button.setAttribute(
        'aria-label',
        saved
          ? `${this.dataset.removeLabel || 'Remove from saved'}: ${title}`
          : `${this.dataset.saveLabel || 'Save product'}: ${title}`
      );
    });
  }

  #createItem(item) {
    const productUrl = safeProductUrl(item.url, location.origin);
    const listItem = document.createElement('li');
    listItem.className = 'scc-saved-products__item';

    const link = document.createElement(productUrl ? 'a' : 'span');
    link.className = 'scc-saved-products__product';
    if (productUrl) link.href = productUrl;

    const imageUrl = safeImageUrl(item.image, location.origin);
    if (imageUrl) {
      const image = document.createElement('img');
      image.src = imageUrl;
      image.alt = item.imageAlt || item.title;
      image.width = 96;
      image.height = 120;
      image.loading = 'lazy';
      link.append(image);
    }

    const title = document.createElement('span');
    title.textContent = item.title;
    link.append(title);

    const remove = document.createElement('button');
    remove.type = 'button';
    remove.className = 'scc-saved-products__remove';
    remove.dataset.sccSavedRemove = item.id;
    remove.textContent = this.dataset.removeButtonLabel || 'Remove';
    remove.setAttribute('aria-label', `${this.dataset.removeLabel || 'Remove from saved'}: ${item.title}`);

    listItem.append(link, remove);
    return listItem;
  }
}

if (globalThis.customElements && !customElements.get('scc-saved-products')) {
  customElements.define('scc-saved-products', SccSavedProducts);
}
